import { NEW_WEAPON_POOL, PASSIVE_POOL, PACT_POOL, PACT_MIN_LEVEL, PACT_MAX_PER_RUN, weightedSample, rollRarity } from '../../data/levelUpPool.js';
import { FUSION_DEFS } from '../../data/fusions.js';

// XP from damage scales with sqrt(amount) rather than a flat per-point rate,
// so a single heavily-multiplied hit (stacked damage passives/meta upgrades)
// earns diminishing XP per point of damage instead of riding the same
// runaway multiplier chain that inflated the hit itself — this is what
// breaks the damage-multiplier-stacking -> faster-leveling -> more-damage-
// picks feedback loop. Spreading the same total damage across many smaller
// hits (e.g. Orbit Ward's ticks) is barely affected, since sqrt is closest
// to linear at small per-hit amounts — only bursty overkill gets flattened.
// Coefficient tuned so a ~20-damage hit (an ordinary early-game weapon tick)
// yields about the same XP the old flat 0.2/damage rate did.
const XP_DAMAGE_SQRT_COEFFICIENT = 0.9;
const XP_PER_SECOND_BASE = 2; // trickle so leveling still happens between hits
const XP_PER_SECOND_GROWTH = 0.02; // XP/sec added per second already survived — the trickle itself ramps up over a long run, not just its cumulative total
// Ceiling on that ramp. Without one the trickle grows without bound, so its
// CUMULATIVE payout is quadratic in run length — which is what let a long
// run (endless loops especially) keep leveling on the clock alone no matter
// how the curve below was tuned. Capped, the trickle tops out at a fixed
// rate and the XP curve gets to decide the pace from there. 10/sec is
// reached at t=400s, i.e. it still ramps across a normal gauntlet run and
// only bites on the very long ones.
const XP_PER_SECOND_MAX = 10;
const XP_PER_MINION_KILL = 5; // flat kill bonus, on top of the damage-based XP already earned whittling it down
const XP_PER_BOSS_KILL = 250; // flat milestone bonus for actually finishing a boss, on top of all the damage-based XP earned fighting it

// Weapon-upgrade level delta by rolled rarity tier — a rare roll is a
// jackpot +3 instead of every weapon upgrade always being a flat, safe +1.
export const WEAPON_UPGRADE_DELTA = { common: 1, uncommon: 2, rare: 3 };

// Ordered so a minRarity floor (see _buildChoices) can compare tiers by
// index — 'pact'/'fusion' deliberately aren't in here, they're always
// treated as clearing any floor (see meetsRarityFloor).
const RARITY_ORDER = ['common', 'uncommon', 'rare'];

// Hard level cap — once every weapon is owned+maxed and every passive is
// picked (_buildChoices() pool empty, ~level 40+ in practice), further
// levels auto-boost HP/damage (see _applyAutoStatBoost) instead of opening
// an unresolvable 0-card pick screen. That auto-boost loop itself needs a
// ceiling so a long enough run doesn't grind forever — 999 is that ceiling;
// Game.js's HUD shows "MAX" in place of the level number once reached.
export const MAX_LEVEL = 999;
const AUTO_STAT_BOOST_PERCENT = 0.01;

/**
 * Parent weapon level at which a Harmonics fusion becomes offerable
 * (BULLET-HEAVEN.md §H4). The parents' max level — deliberately, and after
 * having briefly been 6. That detour is worth recording, because the reasoning
 * that produced it looked exactly like evidence.
 *
 * §H4 says to re-gate "if pacing data shows median runs finish zero fusions",
 * and it did: a flat zero, every run, both pick policies. Two changes went in
 * together — this gate dropped to 6, and an eligible fusion got a guaranteed
 * pick-screen slot (see _buildChoices) instead of competing at FUSION_WEIGHT's
 * real ~3% odds. Reachability jumped, and the gate took the credit.
 *
 * It had not earned it. Measured separately, over 24 simulated gauntlets each:
 *
 *   gate 8, no guaranteed slot ..... 0 / 24
 *   gate 8, guaranteed slot ........ 8 / 24
 *   gate 7, guaranteed slot ........ 16 / 24
 *   gate 6, guaranteed slot ........ 17 / 24
 *
 * The card never being SHOWN was the entire bug. Fixing that alone takes a
 * chorus that never played to one that plays in a third of runs.
 *
 * And the gate was not free. config/constants.js's FUSED_WEAPONS ratios are
 * solved on the bench against BOTH PARENTS AT LEVEL 8 — every fusion is meant
 * to land at ~1.15x the parents it eats, rising to ~1.5x. Against level-6
 * parents, which is what the fusion would actually have consumed, the front
 * four measured 2.5-2.7x on arrival and up to 3.5x at max. That is a 2.3x
 * overshoot of a documented, bench-solved budget, and fusionBalance.test.js
 * could not see it: that test measures the level-8 case, which had quietly
 * stopped being when fusions happen.
 *
 * So: 8, and the reachability fix stays where the problem actually was. If
 * a third of runs ever reads as too rare in play, 7 is the next knob — but
 * re-solve FUSED_WEAPONS against level-7 parents before turning it, rather
 * than repeating this.
 */
export const FUSION_PARENT_LEVEL = 8;


// Cost of the next level. The linear part is the original curve; the
// quadratic term is what stops late levels from arriving faster and faster.
//
// XP income grows superlinearly over a run — more damage events per second
// (fire rate, weapon count, and AoE billing one event per enemy hit), more
// minions per wave, bigger boss HP pools, plus the survival trickle's own
// ramp — while a purely linear cost curve grew at a flat +40. Income
// outrunning cost is exactly the "levels come faster the longer you play"
// shape, and since every level is a card plus a compounding stat boost, it
// fed straight back into power.
//
// `level * (level - 1)` rather than `level * level` so level 1 still costs
// exactly 60 + 40 = 100 and the early curve is untouched to the XP: the
// quadratic only starts mattering once it has levels to square. Roughly:
// L10 622 (was 460), L30 2826 (was 1260), L60 8832 (was 2460).
const XP_QUADRATIC = 1.8;

function xpToNextLevel(level) {
  return 60 + level * 40 + XP_QUADRATIC * level * (level - 1);
}

/**
 * Which mote a minion drops (config/constants.js MOTES.tiers). Purely
 * legibility: the tier picks size and colour, never the XP, which is always
 * whatever that minion actually banked. A player should be able to tell a
 * Splitter's drop from a Swarmling's across the arena without reading a number.
 *
 * The "tougher vocabulary" list is the types whose kill is an event rather
 * than a tick — the ones with a shield, a split, a mute, or Act II's weight.
 */
const MEDIUM_MOTE_TYPES = new Set([
  'splitter', 'warden', 'trapper', 'anglerling', 'mirrorShade', 'muffler', 'sinkweight',
]);

function moteTierFor(minion) {
  return MEDIUM_MOTE_TYPES.has(minion?.type) ? 'medium' : 'small';
}

/**
 * Tracks run XP/level and total damage dealt for end-of-run currency. XP
 * comes from damage dealt (boss and minions alike), minion kills, boss
 * kills, and a survival-time trickle that itself ramps up the longer the
 * run goes. Every level gained opens a 3-choice pick screen built from the
 * player's current weapons/passives; Game.js pauses the simulation while
 * `xp:levelup:open` is pending and resumes it once `resolvePick()` is
 * called by the UI. Once every weapon/passive is exhausted, levels stop
 * opening a pick screen at all and instead silently apply a small HP/damage
 * boost (_applyAutoStatBoost) up to MAX_LEVEL.
 */
export class ProgressionSystem {
  constructor(eventBus, {
    combat, player, isPerkUnlocked = () => false, xpGainMultiplier = 1, levelUpChoices = 3,
    // The run's level-up stream on a seeded run (BULLET-HELL.md §D8) —
    // `Math.random` otherwise. Every offer this system builds draws from it and
    // nothing else does, which is what makes two players on one seed see the
    // same cards in the same order however differently they fought for them.
    rng = Math.random,
  }) {
    this.eventBus = eventBus;
    this.combat = combat;
    this.player = player;
    this.rng = rng;
    this.isPerkUnlocked = isPerkUnlocked;
    this.xpGainMultiplier = xpGainMultiplier;
    // 4 with Glimmer's Lucky Star pet unique (see data/pets.js), else 3.
    this.levelUpChoices = levelUpChoices;

    this.xp = 0;
    this.level = 1;
    this.totalDamageDealt = 0;
    this.minionDamageDealt = 0;
    this.survivalTime = 0;

    this._pendingChoices = [];
    // A single big hit (or the survival trickle plus a hit landing the same
    // tick) can cross more than one level threshold in one _addXp() call.
    // Picks are shown one at a time — this counts how many are still owed
    // so resolvePick() can chain into the next one instead of the UI
    // stacking multiple overlays for thresholds crossed in the same tick.
    this._levelsAwaitingPick = 0;
    // Boolean-flag passives (currently just Adrenaline) are never worth
    // re-picking — PASSIVE_POOL entries aren't deduped by default (the
    // numeric ones stack), so this tracks which stackable:false ones are
    // already owned to skip re-offering them.
    this._ownedNonStackablePassives = new Set();
    // How many pact cards (data/levelUpPool.js PACT_POOL) were accepted this
    // run — _buildChoices stops offering them at PACT_MAX_PER_RUN.
    this._pactsTaken = 0;
    // dashAttack (Kestrel-only, see data/characters.js) isn't a Weapon
    // instance and so has no .level of its own — tracked here instead.
    this._dashAttackLevel = 1;
    // Picked passives (label -> times taken) and pacts, recorded at apply
    // time so the pause screen can show what the run is actually built out
    // of. Weapons aren't recorded here — `combat.weapons` already carries
    // their live levels, including anything a fusion replaced.
    this._passivePicks = new Map();
    this._pactPicks = [];
    // Recipes already announced via `fusion:eligible` — the sting is a "this
    // is now available" event, so it fires once per recipe per run, not every
    // time _buildChoices re-checks eligibility.
    this._announcedFusions = new Set();

    eventBus.on('boss:damaged', ({ amount }) => this._onBossDamage(amount));
    // Tracked separately from totalDamageDealt, not merged into it —
    // totalDamageDealt still feeds calcLeaderboardScore's boss-specific
    // ranking untouched, but calcEchoes now takes minionDamageDealt too (as
    // its own capped credit, not blended into the boss-HP-relative ratio —
    // see Currency.js) so minion damage pays out Echoes same as XP.
    eventBus.on('minion:damaged', ({ amount, minion }) => this._onMinionDamage(amount, minion));
    eventBus.on('minion:died', (payload) => this._onMinionDied(payload));
    // The other half of the mote loop (BULLET-HEAVEN.md §H1): PickupSystem
    // decides when a drop is walked over, this decides what that's worth.
    eventBus.on('mote:collected', ({ xp }) => this.grantMoteXp(xp));
    eventBus.on('boss:died', () => {
      // Flat XP-bar credit — added directly rather than through _addXp, and
      // clamped just under the next threshold, so this bonus can never
      // itself complete a level. Leveling on a boss kill is exclusively
      // _forceLevelUp's job below; without the clamp, a low-level kill
      // (where this flat bonus dwarfs the threshold) could complete a level
      // here AND get the guaranteed one below — 2 levels for 1 kill instead
      // of the intended 1.
      if (this.level < MAX_LEVEL) {
        const cap = xpToNextLevel(this.level) - 1;
        this.xp = Math.min(this.xp + XP_PER_BOSS_KILL * this.xpGainMultiplier, cap);
      }
      // Beating a boss always levels the player up exactly once, independent
      // of where they land on the XP curve — a boss kill should never NOT
      // grant a level just because the fight was fast or the curve's gotten
      // steep, and (per the clamp above) never grants more than one either.
      this._forceLevelUp();
    });
  }

  /**
   * Everything the run has picked up, for the pause screen's build panel
   * (ui/PauseOverlay.js). Weapons are read live from CombatSystem rather
   * than from a pick log, so a fusion shows as the fused weapon at its real
   * level instead of the two parents that no longer exist.
   */
  get buildSummary() {
    const weapons = this.combat.weapons.map((weapon) => ({
      name: weapon.name,
      level: weapon.level,
      maxLevel: weapon.config?.maxLevel ?? 8,
    }));
    // Kestrel's dash strike levels like a weapon but isn't one (see
    // _buildChoices) — surfaced alongside them so its level isn't invisible.
    if (this.combat.dashAttack) {
      weapons.push({ name: 'Dash Strike', level: this._dashAttackLevel, maxLevel: 8 });
    }
    return {
      weapons,
      passives: [...this._passivePicks].map(([label, count]) => ({ label, count })),
      pacts: [...this._pactPicks],
      fusedAway: [...(this.combat.consumedByFusion ?? [])],
      fusions: this.fusionProgress,
    };
  }

  /**
   * Every Harmonics recipe with how close this run is to it — the data behind
   * the pause screen's locked-recipe list (BULLET-HEAVEN.md §H4). A fusion is
   * a discrete, enormous jump, but it was gated behind a condition the player
   * had no way to see coming, which makes it a surprise rather than a chase.
   * You cannot steer your picks toward something you don't know exists.
   *
   * Recipes whose parents are both permanently gone (spent on a DIFFERENT
   * fusion) are dropped: they're dead ends this run, and listing them as
   * "0/6" would read as a goal rather than as a road already not taken.
   */
  get fusionProgress() {
    const rows = [];
    for (const def of FUSION_DEFS) {
      const owned = this.combat.weapons.some((w) => w.name === def.name);
      const parents = def.parents.map((name) => {
        const weapon = this.combat.weapons.find((w) => w.name === name);
        return {
          name,
          level: weapon?.level ?? 0,
          required: this._fusionParentRequirement(name),
          owned: Boolean(weapon),
          spent: Boolean(this.combat.consumedByFusion?.has(name)),
        };
      });
      if (!owned && parents.some((p) => p.spent)) continue;
      rows.push({
        id: def.id,
        name: def.name,
        description: def.description,
        parents,
        owned,
        eligible: this._isFusionEligible(def),
      });
    }
    return rows;
  }

  /** Parent ladders are all 8 long today; `Math.min` keeps the gate sane if one ever isn't. */
  _fusionParentRequirement(name) {
    const weapon = this.combat.weapons.find((w) => w.name === name);
    return Math.min(FUSION_PARENT_LEVEL, weapon?.config?.maxLevel ?? 8);
  }

  /**
   * If levelling `weaponName` advances a *live* recipe, describe it — for the
   * pick card itself (see `_buildChoices`).
   *
   * This is the missing half of BULLET-HEAVEN.md §H4. The pause panel already
   * lists locked recipes and their parent progress, and `fusion:eligible`
   * already announces one coming into reach — but both happen away from the
   * only moment that decides anything, which is the pick screen. The pacing
   * bench made the cost of that gap concrete: an Act I run on the naive
   * "take what's offered" policies ends holding all nine weapons with two of
   * them at level 8, and reaches a fusion **zero times in fifteen runs** —
   * not because the ladder is too long, but because the two it maxed were
   * almost never a recipe pair. There are eight recipes across nine weapons;
   * hitting one by accident is a coin the player never knew they were
   * flipping.
   *
   * So the card says it. "Nova Pulse +2 · Harmonic: 6/8 toward Static Bloom"
   * turns an invisible condition into a legible goal at the instant the
   * player is choosing, which is where every other game in the genre puts
   * its evolution chase. Nothing about the gate, the odds, or the fusion's
   * power changes — only whether the player can see it coming.
   *
   * Reports the recipe *closest to completion*, so a weapon that feeds two
   * recipes advertises the one actually within reach rather than the first
   * one declared.
   */
  _fusionHintFor(weaponName, projectedLevel) {
    let best = null;
    for (const def of FUSION_DEFS) {
      if (!def.parents.includes(weaponName)) continue;
      if (this.combat.weapons.some((w) => w.name === def.name)) continue; // already fused
      // A recipe whose other parent is spent on a different fusion is a dead
      // end this run; pointing at it would be a lie.
      if (def.parents.some((p) => this.combat.consumedByFusion?.has(p))) continue;

      let remaining = 0;
      let otherMissing = false;
      for (const parent of def.parents) {
        const required = this._fusionParentRequirement(parent);
        const weapon = this.combat.weapons.find((w) => w.name === parent);
        const level = parent === weaponName ? projectedLevel : (weapon?.level ?? 0);
        if (parent !== weaponName && !weapon) otherMissing = true;
        remaining += Math.max(0, required - level);
      }
      const candidate = { name: def.name, remaining, otherMissing, weaponName, projectedLevel };
      if (!best || candidate.remaining < best.remaining) best = candidate;
    }
    if (!best) return null;

    const required = this._fusionParentRequirement(weaponName);
    if (best.remaining === 0) return `Harmonic: completes ${best.name}.`;
    // Naming a weapon the player does not own yet is guidance, not noise —
    // "you also need Orbit Ward" is exactly the steer that was missing.
    if (best.otherMissing) return `Harmonic: ${best.name} — also needs its other parent.`;
    return `Harmonic: ${Math.min(best.projectedLevel, required)}/${required} toward ${best.name}.`;
  }

  _isFusionEligible(def) {
    if (this.combat.weapons.some((w) => w.name === def.name)) return false; // already fused
    return def.parents.every((name) => this.combat.weapons.some(
      (w) => w.name === name && w.level >= this._fusionParentRequirement(name),
    ));
  }

  /**
   * Fires `fusion:eligible` the first time each recipe comes into reach.
   *
   * Called at pick-apply time rather than at card-build time, deliberately:
   * the announcement has to land BEFORE the fusion card shows up, or it's a
   * caption on something the player is already looking at. Applying the pick
   * that levels the second parent is the exact moment the chorus becomes
   * available, and `resolvePick` calls this between `apply()` and any chained
   * `_openLevelUp()`, so the sting plays into the card rather than over it.
   */
  _announceNewlyEligibleFusions() {
    for (const def of FUSION_DEFS) {
      if (this._announcedFusions.has(def.id)) continue;
      if (!this._isFusionEligible(def)) continue;
      this._announcedFusions.add(def.id);
      this.eventBus.emit('fusion:eligible', {
        id: def.id, name: def.name, parents: [...def.parents], description: def.description,
      });
    }
  }

  /** 0..1 progress toward the next level — read by Game.js's vertical XP bar HUD. */
  get xpProgress() {
    if (this.level >= MAX_LEVEL) return 1;
    return this.xp / xpToNextLevel(this.level);
  }

  /** Read by Game.js's HUD to show "MAX" instead of a level number once capped. */
  get isMaxLevel() {
    return this.level >= MAX_LEVEL;
  }

  /** Not called while Game is paused (level-up pick open / run ended). */
  update(dt) {
    this.survivalTime += dt;
    const xpPerSecond = Math.min(
      XP_PER_SECOND_MAX,
      XP_PER_SECOND_BASE + XP_PER_SECOND_GROWTH * this.survivalTime,
    );
    this._addXp(xpPerSecond * dt * this.xpGainMultiplier);
  }

  _onBossDamage(amount) {
    this.totalDamageDealt += amount;
    this._addXp(Math.sqrt(amount) * XP_DAMAGE_SQRT_COEFFICIENT * this.xpGainMultiplier);
  }

  /**
   * Minion damage-XP is BANKED on the minion rather than credited on the spot
   * (BULLET-HEAVEN.md §H1) — it rides the mote the kill drops and is paid when
   * the player walks over it. The total is unchanged; only its arrival moves.
   *
   * Falls back to crediting immediately when the payload carries no entity.
   * That is not defensive padding, it is the herald path: Boss.js dual-emits
   * `minion:damaged` for minibosses (so they read as minions to every listener
   * that cares) and deliberately does NOT pass `minion`, because a boss is a
   * duel, not a harvest. Same reason boss damage never moted.
   */
  _onMinionDamage(amount, minion) {
    this.minionDamageDealt += amount;
    const xp = Math.sqrt(amount) * XP_DAMAGE_SQRT_COEFFICIENT * this.xpGainMultiplier;
    if (minion) {
      minion.xpBanked = (minion.xpBanked ?? 0) + xp;
    } else {
      this._addXp(xp);
    }
  }

  /**
   * Converts a kill into a drop. The mote carries the flat kill bonus plus
   * everything banked whittling that minion down, so a minion is worth exactly
   * what it was worth before — the difference is that it's now an object on
   * the floor, and collecting it is the same input as dodging.
   */
  _onMinionDied({ x, y, minion } = {}) {
    const killBonus = XP_PER_MINION_KILL * this.xpGainMultiplier;
    if (!minion) {
      // Herald (see _onMinionDamage) — pays straight into the bar as before.
      this._addXp(killBonus);
      return;
    }
    const xp = (minion.xpBanked ?? 0) + killBonus;
    minion.xpBanked = 0;
    this.eventBus.emit('mote:dropped', { x, y, xp, tier: moteTierFor(minion) });
  }

  /** Paid when a mote is walked over. Public so the pacing bench can drive it directly. */
  grantMoteXp(amount) {
    if (amount > 0) this._addXp(amount);
  }

  /**
   * Drops motes for damage already banked on minions that are about to be
   * removed without dying — the stage-transition sweep (Game.js) and, since
   * the ramp ends on a clock rather than on a clear (BULLET-HEAVEN.md §H2),
   * every wave stage's leftovers.
   *
   * Without this, chipping a Sinkweight for ten seconds and then having the
   * stage expire would silently delete that XP. The kill bonus is NOT paid —
   * nothing was killed — only what the player actually earned hitting it.
   */
  flushBankedXp(minions) {
    for (const minion of minions) {
      const banked = minion.xpBanked ?? 0;
      if (banked <= 0) continue;
      minion.xpBanked = 0;
      this.eventBus.emit('mote:dropped', { x: minion.x, y: minion.y, xp: banked, tier: moteTierFor(minion) });
    }
  }

  _addXp(amount) {
    if (this.level >= MAX_LEVEL) return; // fully capped — no more XP to track
    this.xp += amount;
    while (this.level < MAX_LEVEL && this.xp >= xpToNextLevel(this.level)) {
      this.xp -= xpToNextLevel(this.level);
      this.level++;
      // Every level now grants a flat +1% stat boost automatically, on top
      // of whatever card is picked below — not just once the weapon/passive
      // pool runs dry (see _applyAutoStatBoost).
      this._applyAutoStatBoost();
      this._levelsAwaitingPick++;
      // Fires on every level gained, unlike xp:levelup:open (only when a
      // pick screen actually shows — never emitted once the pool exhausts
      // into the auto-boost path). Game.js listens for this to pop the
      // "LEVEL UP!" text over the player regardless of which path handles it.
      this.eventBus.emit('xp:levelup:gained', { level: this.level });
    }
    // Only open a new pick screen if one isn't already showing — if several
    // levels were gained in this call, the rest chain in via resolvePick().
    if (this._levelsAwaitingPick > 0 && this._pendingChoices.length === 0) {
      this._openLevelUp();
    }
  }

  /**
   * Grants exactly one level, bypassing the XP curve entirely — used by the
   * boss:died handler above so a boss kill always levels the player up
   * regardless of how much XP the fight itself produced. Shares
   * _addXp's queuing/pick-opening logic (levelsAwaitingPick, chaining
   * through an already-open pick) so it composes cleanly with a same-tick
   * XP-threshold level-up rather than fighting it for a pick slot.
   *
   * `minRarity` floors the card pool this specific level-up offers —
   * defaults to 'uncommon' so a boss kill's guaranteed level is always at
   * least an uncommon-tier reward, never a plain common roll.
   */
  _forceLevelUp(minRarity = 'uncommon') {
    if (this.level >= MAX_LEVEL) return; // fully capped — nothing left to grant
    this.level++;
    this._applyAutoStatBoost();
    this._levelsAwaitingPick++;
    this.eventBus.emit('xp:levelup:gained', { level: this.level });
    if (this._levelsAwaitingPick > 0 && this._pendingChoices.length === 0) {
      this._openLevelUp(minRarity);
    }
  }

  _openLevelUp(minRarity = null) {
    const choices = this._buildChoices(minRarity);
    if (choices.length === 0) {
      // Every weapon owned+maxed and every passive already picked — nothing
      // left to offer as a card. The flat +1% stat boost already landed
      // when this level was granted (_addXp/_forceLevelUp above), so this
      // just skips a 0-card pick screen (which Game.js's autopause would
      // leave stuck forever, since there'd be nothing to click to resolve
      // it) and keeps draining any other levels awaiting a pick the same way.
      this._levelsAwaitingPick--;
      if (this._levelsAwaitingPick > 0) this._openLevelUp();
      return;
    }
    this._pendingChoices = choices;
    this.eventBus.emit('xp:levelup:open', { level: this.level, choices });
  }

  /** +1% max HP (heals to match) and +1% weapon damage — applied automatically on every level gained (see _addXp/_forceLevelUp), on top of whatever card the player picks. */
  _applyAutoStatBoost() {
    const bonus = this.player.maxHp * AUTO_STAT_BOOST_PERCENT;
    this.player.maxHp += bonus;
    this.player.hp = Math.min(this.player.maxHp, this.player.hp + bonus);
    this.combat.damageMultiplier = (this.combat.damageMultiplier ?? 1) * (1 + AUTO_STAT_BOOST_PERCENT);
  }

  /** Called by the LevelUpPicker UI when the player clicks a choice. */
  resolvePick(choiceId) {
    const choice = this._pendingChoices.find((c) => c.id === choiceId);
    if (choice) choice.apply();
    this._pendingChoices = [];
    this._levelsAwaitingPick--;
    // Before the chain below: if that pick was the one that brought a fusion
    // into reach, the announcement has to precede the card offering it.
    this._announceNewlyEligibleFusions();
    this.eventBus.emit('xp:levelup:closed', {});
    if (this._levelsAwaitingPick > 0) this._openLevelUp();
  }

  /**
   * Resolves any pending level-up pick(s) with the first available choice
   * instead of leaving them dangling. Needed because a boss can die (or the
   * player can die) on the exact tick a level-up threshold is crossed —
   * `boss:damaged` grants XP before `boss:died` fires — leaving a pick
   * screen open with no further input coming. Game.js calls this before
   * ending the run or advancing the gauntlet so `_pendingChoices` never
   * gets stuck non-empty (which would otherwise permanently block every
   * later level-up this run, per _addXp's `_pendingChoices.length === 0`
   * guard) and the player still gets *something* for the level rather than
   * losing it outright.
   */
  forceResolvePending() {
    let resolvedAny = false;
    const grantedLabels = [];
    while (this._levelsAwaitingPick > 0) {
      if (this._pendingChoices.length === 0) this._pendingChoices = this._buildChoices();
      if (this._pendingChoices.length > 0) {
        this._pendingChoices[0].apply();
        grantedLabels.push(this._pendingChoices[0].label);
      }
      // else: no card available (every weapon/passive maxed) — the flat
      // +1% stat boost already landed when this level was granted (see
      // _addXp/_forceLevelUp), so there's nothing further to force here.
      this._pendingChoices = [];
      this._levelsAwaitingPick--;
      resolvedAny = true;
    }
    // A forced resolution can level a parent too. No card will follow it (the
    // run is ending or the stage is advancing), but the pause panel's recipe
    // list reads the same eligibility, so keep the two in step.
    this._announceNewlyEligibleFusions();
    // Unlike resolvePick(), a forced resolution never gets a chance to emit
    // xp:levelup:closed itself — which is what Game.js's _autoPause listener
    // relies on to unpause. Without this, a run ending on the same tick as a
    // level-up threshold being crossed leaves the game permanently paused
    // with no UI left to dismiss it.
    if (resolvedAny) {
      if (grantedLabels.length > 0) {
        // The player never got to see (let alone pick) these — surfaced as
        // floating text instead (JuiceDirector) so a run-ending level-up
        // doesn't read as "leveled up, got nothing" even though a card
        // silently applied.
        this.eventBus.emit('xp:levelup:auto-resolved', { labels: grantedLabels });
      }
      this.eventBus.emit('xp:levelup:closed', {});
    }
  }

  /**
   * Seeds a deterministic, UI-free build for Boss Challenge. Challenge fights
   * skip wave XP and level-up screens, so starting every boss at level 1 made
   * late bosses a test of patience rather than a test of their mechanics.
   * Normal runs never call this method.
   */
  prepareChallenge(targetLevel) {
    const target = Math.max(1, Math.min(MAX_LEVEL, Math.floor(targetLevel)));
    this._pendingChoices = [];
    this._levelsAwaitingPick = 0;
    this.xp = 0;
    while (this.level < target) {
      this.level++;
      this._applyAutoStatBoost();

      // Add the second and third weapon at predictable milestones, then
      // spread the remaining levels across the current kit. This keeps every
      // challenge self-contained and avoids silently opening a level-up UI.
      if (this.level === 5 || this.level === 9) {
        const next = NEW_WEAPON_POOL.find((def) => !this.combat.weapons.some((w) => w.name === def.name));
        if (next) this.combat.addWeapon(next.create());
      }
      const upgradeable = this.combat.weapons.filter((weapon) => weapon.level < (weapon.config?.maxLevel ?? 8));
      if (upgradeable.length > 0) {
        upgradeable.sort((a, b) => a.level - b.level);
        upgradeable[0].levelUp();
      }
    }
    // Anything this seeded build already qualifies for is its STARTING state,
    // not something the player just achieved — mark it announced so the
    // "a Harmonic is within reach" sting doesn't fire for a condition that was
    // true before the first frame. A recipe reached later still announces.
    for (const def of FUSION_DEFS) {
      if (this._isFusionEligible(def)) this._announcedFusions.add(def.id);
    }
    return this.level;
  }

  /**
   * @param {string|null} minRarity - floors every rolled/fixed-rarity entry
   *   in the pool at this tier or better (see RARITY_ORDER) — pact/fusion
   *   entries always clear it. Used by _forceLevelUp so a boss kill's
   *   guaranteed level is never a plain common reward; left null for every
   *   other level-up, which keeps the normal common-heavy odds.
   */
  _buildChoices(minRarity = null) {
    // Fortune's Favor (Shard perk, see data/shardUnlocks.js's luckyOdds) —
    // doubles the odds of uncommon/rare showing up, both here and in the
    // weapon-upgrade rarity roll just below.
    const luckyOdds = this.isPerkUnlocked('luckyOdds');
    const pool = [];

    const floorRarity = (rarity) => (minRarity && RARITY_ORDER.indexOf(rarity) < RARITY_ORDER.indexOf(minRarity)
      ? minRarity : rarity);
    const meetsFloor = (rarity) => {
      if (!minRarity) return true;
      const idx = RARITY_ORDER.indexOf(rarity);
      return idx === -1 || idx >= RARITY_ORDER.indexOf(minRarity); // pact/fusion always clear it
    };

    for (const weapon of this.combat.weapons) {
      // Fused weapons (config.maxLevel) climb a shorter ladder than the
      // base weapons' 8 — same upgrade cards, different ceiling.
      const maxLevel = weapon.config?.maxLevel ?? 8;
      if (weapon.level >= maxLevel) continue;
      const rarity = floorRarity(rollRarity(luckyOdds, this.rng));
      const delta = Math.min(WEAPON_UPGRADE_DELTA[rarity], maxLevel - weapon.level);
      const reachesMax = weapon.level + delta >= maxLevel;
      pool.push({
        id: `upgrade:${weapon.name}`,
        label: reachesMax ? `${weapon.name} MAX` : `${weapon.name} +${delta}`,
        rarity,
        description: reachesMax
          ? `Level up ${weapon.name} to its max level.`
          : `Level up ${weapon.name} to level ${weapon.level + delta}.`,
        fusionHint: this._fusionHintFor(weapon.name, weapon.level + delta),
        apply: () => weapon.levelUp(delta),
      });
    }

    // Kestrel-only: dashAttack is a plain {damage, radius} object, not a
    // Weapon, so it's null (falsy) for every other character — this block
    // is a no-op for them. Mutating its fields at pick-time takes effect
    // immediately since CombatSystem._applyDashAoe reads them fresh on
    // every dash hit.
    if (this.combat.dashAttack && this._dashAttackLevel < 8) {
      const rarity = floorRarity(rollRarity(luckyOdds, this.rng));
      const delta = Math.min(WEAPON_UPGRADE_DELTA[rarity], 8 - this._dashAttackLevel);
      const reachesMax = this._dashAttackLevel + delta >= 8;
      pool.push({
        id: 'upgrade:dashAttack',
        label: reachesMax ? 'Dash Strike MAX' : `Dash Strike +${delta}`,
        rarity,
        description: reachesMax
          ? `Dash Strike: ${this.combat.dashAttack.damage} -> ${this.combat.dashAttack.damage + delta * 8} damage (max level).`
          : `Dash Strike: ${this.combat.dashAttack.damage} -> ${this.combat.dashAttack.damage + delta * 8} damage.`,
        apply: () => {
          this.combat.dashAttack.damage += delta * 8; // 60 -> 124 at lvl8
          this.combat.dashAttack.radius += delta * 1.5; // 22 -> 34 at lvl8
          this._dashAttackLevel += delta;
        },
      });
    }

    for (const def of NEW_WEAPON_POOL) {
      const owned = this.combat.weapons.some((w) => w.name === def.name);
      // A weapon spent as a fusion parent is gone from `weapons` but must not
      // come back as a "New:" card — re-acquiring Spirit Lance after it fused
      // into Storm Lance reads as the fusion failing to consume anything, and
      // could re-open a second fusion that shares that parent.
      const fusedAway = this.combat.consumedByFusion?.has(def.name);
      if (owned || fusedAway || !meetsFloor(def.rarity)) continue;
      pool.push({
        id: `new:${def.name}`,
        label: `New: ${def.name}`,
        rarity: def.rarity,
        description: def.description,
        // Taking a weapon is the first step of a recipe as much as levelling
        // one is — and it is the step where "which of these nine do I care
        // about" is most opaque.
        fusionHint: this._fusionHintFor(def.name, 1),
        apply: () => this.combat.addWeapon(def.create()),
      });
    }

    for (const passive of PASSIVE_POOL) {
      if (passive.stackable === false && this._ownedNonStackablePassives.has(passive.id)) continue;
      if (!meetsFloor(passive.rarity)) continue;
      pool.push({
        id: `passive:${passive.id}`,
        label: passive.label,
        rarity: passive.rarity,
        description: passive.description,
        apply: () => {
          passive.apply(this.player, this.combat);
          if (passive.stackable === false) this._ownedNonStackablePassives.add(passive.id);
          this._passivePicks.set(passive.label, (this._passivePicks.get(passive.label) ?? 0) + 1);
        },
      });
    }

    // Harmonics (data/fusions.js): offered once both parents reach
    // FUSION_PARENT_LEVEL and the fusion isn't already owned. Accepting
    // replaces both parents with the fused weapon (CombatSystem.fuseWeapons).
    //
    // Held OUT of `pool` and given a guaranteed slot below rather than
    // competing in the weighted draw. levelUpPool.js's FUSION_WEIGHT of 12 is
    // documented as "deliberately high", and per entry it is — but it is
    // weighted against a pool that by mid-run holds twenty-odd entries
    // totalling well over a thousand weight, so a fusion's real odds of
    // appearing on any given pick screen were about 3%. The pacing bench
    // caught this: runs that reached eligibility with eight cards still to
    // come were finishing having never once been SHOWN the fusion. A number
    // that reads high next to `rare: 5` and is actually a rounding error
    // against the whole pool is precisely the class of silent bug
    // BULLET-HEAVEN.md §H5 exists to catch.
    const fusionChoices = [];
    for (const def of FUSION_DEFS) {
      if (!this._isFusionEligible(def)) continue;
      fusionChoices.push({
        id: `fusion:${def.id}`,
        label: `Harmonize: ${def.name}`,
        rarity: 'fusion',
        kind: 'fusion',
        description: `${def.parents[0]} + ${def.parents[1]} become one. ${def.description}`,
        apply: () => this.combat.fuseWeapons(def),
      });
    }

    // Pacts (PACT_POOL): boon+drawback gambles. Gated past the opening
    // levels so the first picks stay simple, and capped per run so the
    // drawbacks can't stack into an unwinnable state. Accepted pacts join
    // the same dedupe set as passives — never re-offered.
    if (this.level >= PACT_MIN_LEVEL && this._pactsTaken < PACT_MAX_PER_RUN) {
      for (const pact of PACT_POOL) {
        if (this._ownedNonStackablePassives.has(pact.id)) continue;
        pool.push({
          id: `pact:${pact.id}`,
          label: pact.label,
          rarity: pact.rarity,
          kind: 'pact',
          boonText: pact.boonText,
          costText: pact.costText,
          description: pact.description,
          apply: () => {
            pact.apply(this.player, this.combat);
            this._ownedNonStackablePassives.add(pact.id);
            this._pactsTaken++;
            this._pactPicks.push({ label: pact.label, boonText: pact.boonText, costText: pact.costText });
          },
        });
      }
    }

    // One guaranteed fusion slot when any recipe is eligible — the chorus
    // arrives on the very next card, which is what makes it a chase (the
    // pause panel showed the player it was coming; this is it arriving).
    // Exactly one, even with several eligible: a pick screen made of nothing
    // but fusions is a different, worse decision than "the Harmonic, or
    // something else". The rest still ride the normal draw, so a second
    // eligible recipe is never hidden for long.
    const guaranteed = fusionChoices.length > 0
      ? [fusionChoices[Math.floor(this.rng() * fusionChoices.length)]]
      : [];
    pool.push(...fusionChoices.filter((c) => c !== guaranteed[0]));

    // The step *before* that one. When a recipe is a single upgrade card from
    // eligibility, guarantee that upgrade a slot too.
    //
    // Without this, the last rung is the flakiest: the player has done all
    // the work, the pause panel reads "7/8 + 8/8", and whether the run ever
    // sees its Harmonic comes down to one specific weapon winning one
    // weighted draw against twenty-odd other entries — repeatedly, until the
    // run ends. That is the same rounding-error failure the guaranteed fusion
    // slot above was added to fix, one card earlier, and it is why the
    // announcement could fire and the payoff still never arrive.
    //
    // Only the final rung, and only one: earlier rungs are the actual choice
    // this system is about ("do I commit to this recipe?"), and taking that
    // choice away would make every run's build the same.
    if (guaranteed.length === 0) {
      const finisher = pool.find((c) => c.id.startsWith('upgrade:') && c.fusionHint?.startsWith('Harmonic: completes '));
      if (finisher) {
        guaranteed.push(finisher);
        pool.splice(pool.indexOf(finisher), 1);
      }
    }
    const remaining = weightedSample(pool, Math.max(0, this.levelUpChoices - guaranteed.length), luckyOdds, this.rng);
    return [...guaranteed, ...remaining];
  }
}
